
import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';

const RadexPartnership = () => {
  const { t } = useLanguage();

  return (
    <section id="partnership" className="py-20 relative overflow-hidden">
      {/* Background image with overlay - Swapped with RadexFeatures.tsx background */}
      <div 
        className="absolute inset-0 bg-cover bg-center z-0" 
        style={{ 
          backgroundImage: "url('/background_images/radex_partnership.jpg')",
          opacity: 0.15
        }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t('radex.partnership.title') || "The Club & Radex Markets Partnership"}
            </h2>
            <p className="text-xl text-gray-300 mb-6">
              {t('radex.partnership.description') || "Our strategic partnership with Radex Markets enables us to offer unique investment opportunities to our club members, combining our expertise with a regulated and trusted broker."}
            </p>
            <p className="text-gray-400 mb-8">
              {t('radex.partnership.details') || "Through this collaboration, members of The Club gain access to exclusive trading conditions, enhanced account management, and specialized support from both organizations. The Radex Markets and The Club partnership is built on shared values of transparency, security, and delivering exceptional value to our community."}
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-transparent border-2 border-purple-500 text-purple-400 hover:bg-purple-500/10 px-8 py-2.5 rounded-full text-base font-medium shadow-lg hover:shadow-xl transition-all"
                asChild
              >
                <a 
                  href="https://www.radexmarkets.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <span>{t('radex.partnership.visitRadex') || "Visit Radex Markets"}</span>
                  <ExternalLink size={16} />
                </a>
              </Button>
            </div>
          </div>
          
          <div className="relative">
            <div className="bg-gray-800/70 backdrop-blur-sm border border-gray-700 p-8 rounded-lg shadow-xl relative z-10">
              <h3 className="text-2xl font-bold text-white mb-6">
                {t('radex.partnership.benefitsTitle') || "Partnership Benefits"}
              </h3>
              <ul className="space-y-5">
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="font-semibold text-purple-400">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">
                      {t('radex.partnership.benefit1Title') || "Enhanced Security"}
                    </h4>
                    <p className="text-gray-400">
                      {t('radex.partnership.benefit1Text') || "Trade with confidence knowing your funds are protected by Radex Markets' robust security protocols."}
                    </p>
                  </div>
                </li>
                
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="font-semibold text-purple-400">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">
                      {t('radex.partnership.benefit2Title') || "Exclusive Products"}
                    </h4>
                    <p className="text-gray-400">
                      {t('radex.partnership.benefit2Text') || "Access specialized investment products and trading conditions available only to Club members."}
                    </p>
                  </div>
                </li>
                
                <li className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <span className="font-semibold text-purple-400">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">
                      {t('radex.partnership.benefit3Title') || "Professional Support"}
                    </h4>
                    <p className="text-gray-400">
                      {t('radex.partnership.benefit3Text') || "Receive dedicated assistance from both The Club and Radex Markets teams for an enhanced trading experience."}
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            
            {/* Decorative elements */}
            <div className="absolute top-10 -right-6 w-32 h-32 bg-purple-600/30 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-purple-600/20 rounded-full blur-3xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RadexPartnership;
